import React from "react";
import { Box, Divider } from "@mantine/core";
import { tokens } from "@blurb/codex-react";
import { useParams } from "react-router-dom";
import { GraphQL } from "@rpi/openapi-api";
import { OrderItemCard } from "../../../components/OrderItemCard";
import { Label } from "../../../components/Typography";
import { useAuthUser } from "../../../hooks/useAuthUser";
import { OrderTabContent } from "./OrderTabContent";

function formatItemCount(count: number): string {
  return count === 1 ? "1 Item" : `${count} Items`;
}

export const OrderItemsTab: React.FC = () => {
  const { customerPublicId, customerOrderId } = useParams();
  const user = useAuthUser();
  const hasOrderIdentifier = !!customerPublicId && !!customerOrderId;

  const { data, isLoading, error } = GraphQL.useOrderItemsQuery(
    {
      orderIdentifier: {
        customerPublicId: customerPublicId ?? "",
        customerOrderId: customerOrderId ?? "",
      },
      isSandbox: user.isSandbox,
    },
    { enabled: hasOrderIdentifier }
  );

  const orderItems = data?.orderByIdentifier?.orderItems ?? [];
  const currency = data?.orderByIdentifier?.currency ?? GraphQL.SupportedCurrency.Usd;

  return (
    <OrderTabContent
      data={orderItems}
      isLoading={isLoading}
      error={error}
      emptyMessage="No items found for this order."
    >
      {(items) => (
        <Box sx={{ padding: tokens.spacing[4], display: "flex", flexDirection: "column", gap: tokens.spacing[6] }}>
          <Label>{formatItemCount(items.length)}</Label>
          <Divider sx={{ borderColor: tokens.color.semantic.bg.surfaceSunken }} />
          {items.map((item, index) => (
            <React.Fragment key={item.id}>
              {index > 0 && <Divider sx={{ borderColor: tokens.color.semantic.bg.surfaceSunken }} />}
              <OrderItemCard orderItem={item} currency={currency} isSuperAdmin={user.is.SUPER_ADMIN} />
            </React.Fragment>
          ))}
        </Box>
      )}
    </OrderTabContent>
  );
};
